/**
 * Collection filtering / sorting / pagination without full page loads.
 *
 * Uses Shopify's Section Rendering API (`?section_id=`) to re-render only the
 * collection section, then swaps the regions that actually change. Everything
 * degrades to a normal navigation if JS is off or the request fails.
 */
(function () {
  // Regions replaced on every update. The filter sidebar lives inside the layout,
  // so facet counts and active states refresh along with the product grid.
  const REGIONS = ['.collection-page__layout', '.collection-bar__count'];
  const BUSY_CONTAINER = '.collection-page';

  let historyBound = false;

  function stripEmpty(params) {
    Array.from(params.keys()).forEach((key) => {
      if (params.get(key) === '') params.delete(key);
    });
    return params;
  }

  function withQuery(path, params) {
    const query = stripEmpty(params).toString();
    return query ? `${path}?${query}` : path;
  }

  function setBusy(state) {
    const container = document.querySelector(BUSY_CONTAINER);
    if (!container) return;
    if (state) container.setAttribute('aria-busy', 'true');
    else container.removeAttribute('aria-busy');
  }

  async function render(url, sectionId, options) {
    const settings = options || {};
    const separator = url.indexOf('?') === -1 ? '?' : '&';

    setBusy(true);

    try {
      const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      REGIONS.forEach((selector) => {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);
        // Replacing the layout re-creates the custom element, which re-binds itself.
        if (next && current) current.replaceWith(next);
      });

      if (settings.push !== false) {
        window.history.pushState({ collectionUrl: url }, '', url);
      }

      setBusy(false);

      if (settings.scroll) {
        const container = document.querySelector(BUSY_CONTAINER);
        if (container) container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      // Never leave the customer with a dead filter — fall back to a real load.
      window.location.href = url;
    }
  }

  // Below this width the sidebar stacks above the product grid, so a fully expanded
  // filter pushes the products off the first screen. Matches the CSS breakpoint that
  // turns the sidebar into a sticky column.
  const SIDEBAR_BREAKPOINT = '(min-width: 1000px)';

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.collectionUrl = this.dataset.collectionUrl;
      this.sectionId = this.dataset.sectionId;

      this.filterForm = this.querySelector('[data-filter-form]');
      this.clearLink = this.querySelector('[data-filter-clear]');
      this.setUpCollapse();
      this.sortForm = document.querySelector('[data-sort-form]');
      this.sortSelect = document.querySelector('[data-sort-select]');
      this.pagination = document.querySelector('.collection-page__main .pagination');

      // Filters apply only on submit, so several facets can be picked first.
      if (this.filterForm) {
        this.filterForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.go(withQuery(this.collectionUrl, this.filterParams()));
        });
      }

      if (this.clearLink) {
        this.clearLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.go(this.clearLink.getAttribute('href'));
        });
      }

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => this.go(this.sortUrl()));
      }
      if (this.sortForm) {
        this.sortForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.go(this.sortUrl());
        });
      }

      if (this.pagination) {
        this.pagination.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link) return;
          event.preventDefault();
          this.go(link.getAttribute('href'), { scroll: true });
        });
      }

      // Bound once on window, so it must survive the element being replaced.
      if (!historyBound && this.sectionId) {
        historyBound = true;
        const sectionId = this.sectionId;
        window.addEventListener('popstate', () => {
          render(window.location.pathname + window.location.search, sectionId, { push: false });
        });
      }
    }

    /**
     * Progressive enhancement: the panel is open in the markup, and only becomes
     * collapsible once this runs. Starts expanded when a filter is already applied, so
     * a shopper arriving on a filtered URL can see what is narrowing the results.
     */
    setUpCollapse() {
      const toggle = this.querySelector('[data-filter-toggle]');
      const body = this.querySelector('[data-filter-body]');
      if (!toggle || !body) return;

      this.classList.add('collection-filters--collapsible');

      const wide = window.matchMedia(SIDEBAR_BREAKPOINT);
      const hasActiveFilters = Boolean(this.clearLink);

      const setExpanded = (expanded) => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        // A class, not `hidden`: the panel animates its height in CSS, and `hidden`
        // would snap it shut. `inert` keeps the collapsed fields out of the tab order
        // and off the accessibility tree, which is what `hidden` was there for.
        this.classList.toggle('is-collapsed', !expanded);
        if (expanded) body.removeAttribute('inert');
        else body.setAttribute('inert', '');
      };

      // Above the breakpoint the panel is forced open by CSS, so leave `hidden` off
      // and keep aria-expanded honest about what is actually on screen.
      const sync = () => setExpanded(wide.matches || hasActiveFilters);

      sync();
      wide.addEventListener('change', sync);

      toggle.addEventListener('click', () => {
        if (wide.matches) return;
        setExpanded(toggle.getAttribute('aria-expanded') === 'false');
      });
    }

    filterParams() {
      const params = new URLSearchParams(new FormData(this.filterForm));
      if (this.sortSelect) params.set('sort_by', this.sortSelect.value);
      return params;
    }

    // Sorting acts on its own, so it rebuilds from the params already applied in
    // the URL rather than from the form — otherwise it would commit filter changes
    // the customer has ticked but not yet applied.
    sortUrl() {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', this.sortSelect.value);
      return withQuery(window.location.pathname, params);
    }

    go(url, options) {
      if (!this.sectionId) {
        window.location.href = url;
        return;
      }
      render(url, this.sectionId, options);
    }
  }

  if (!customElements.get('collection-filters-component')) {
    customElements.define('collection-filters-component', CollectionFilters);
  }
})();
